/**
 * Spin Result Modal - Celebration modal for daily spin results
 */

import { useEffect, useState } from 'react';
import { X, PartyPopper, Frown } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import type { SpinResult } from '../../types';
import { getSpinResultDisplay, getConfettiColors } from '../../utils/slot';

interface Props {
  result: SpinResult;
  onClose: () => void;
}

// Pre-generate random values for confetti particles (generated once on mount)
function useConfettiParticles(count: number) {
  // Use useState initializer to generate random values only once
  const [particles] = useState(() =>
    Array.from({ length: count }).map(() => ({
      left: `${Math.random() * 100}%`,
      duration: `${1500 + Math.random() * 1000}ms`,
    }))
  );
  return particles;
}

// Simple confetti particle
function ConfettiParticle({ color, delay, left, duration }: { color: string; delay: number; left: string; duration: string }) {
  return (
    <div
      className="absolute w-3 h-3 rounded-sm animate-confetti"
      style={{
        backgroundColor: color,
        left,
        animationDelay: `${delay}ms`,
        animationDuration: duration,
      }}
    />
  );
}

export default function SpinResultModal({ result, onClose }: Props) {
  const { t } = useLanguage();
  const [showConfetti, setShowConfetti] = useState(false);
  const confettiParticles = useConfettiParticles(50);

  const display = getSpinResultDisplay(result);
  const confettiColors = getConfettiColors(result.prize_type);
  const isWin = display.isWin;

  useEffect(() => {
    if (isWin) {
      // Trigger confetti after a short delay
      const timer = setTimeout(() => {
        setShowConfetti(true);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isWin]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        role="button"
        tabIndex={0}
        aria-label={t('modal.close', 'Close modal')}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
      />

      {/* Confetti */}
      {showConfetti && isWin && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {confettiParticles.map((particle, i) => (
            <ConfettiParticle
              key={i}
              color={confettiColors[i % confettiColors.length]}
              delay={i * 50}
              left={particle.left}
              duration={particle.duration}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      <div
        className={`relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center transform transition-all duration-500 ${
          isWin ? 'animate-bounce-in' : 'animate-fade-in'
        }`}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Icon */}
        <div
          className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center mb-4 ${
            isWin
              ? 'bg-gradient-to-br from-action-light to-action-dark shadow-lg shadow-action-light/50'
              : 'bg-gray-100'
          }`}
        >
          {isWin ? (
            <span className="text-5xl animate-pulse">{display.icon}</span>
          ) : (
            <Frown className="w-12 h-12 text-gray-400" />
          )}
        </div>

        {/* Result Text */}
        <h2 className={`text-2xl font-bold mb-2 ${display.color}`}>
          {display.title}
        </h2>
        <p className="text-gray-600 mb-6">{display.subtitle}</p>

        {/* Prize Details */}
        {isWin && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            {result.multiplier > 1 && (
              <div className="flex items-center justify-center gap-2 mb-2">
                <span className="text-sm text-gray-500">{t('spinResult.multiplier', 'Multiplier:')}</span>
                <span className="font-bold text-action-dark">{result.multiplier}x</span>
              </div>
            )}

            {result.prize_type !== 'nothing' && result.prize_type !== 'xp' && (
              <div className="flex items-center justify-center gap-3">
                {result.base_rumios !== result.rumios_won && (
                  <span className="text-gray-400 line-through">{result.base_rumios}</span>
                )}
                <span className="text-3xl font-bold text-gray-900">
                  +{result.rumios_won}
                </span>
              </div>
            )}

            {result.xp_won > 0 && (
              <div className="mt-2 text-sm text-gray-500">
                +{result.xp_won} XP {t('spinResult.earned', 'earned')}
              </div>
            )}
          </div>
        )}

        {/* Pro Pass Special */}
        {result.prize_type === 'pro_pass' && (
          <div className="bg-gradient-to-r from-action-light/10 to-action-dark/10 border border-action-light rounded-xl p-4 mb-6">
            <PartyPopper className="w-8 h-8 mx-auto text-action mb-2" />
            <p className="font-medium text-gray-900">
              {t('spinResult.proPassTitle', 'You won a 24-hour Pro Pass!')}
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {t('spinResult.proPassDescription', 'Enjoy Pro benefits until tomorrow. See deals 24 hours earlier!')}
            </p>
          </div>
        )}

        {/* Action */}
        <button
          onClick={onClose}
          className={`w-full px-6 py-3 font-bold rounded-xl transition-colors ${
            isWin
              ? 'bg-primary-600 text-white hover:bg-primary-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {isWin
            ? t('spinResult.awesome', 'Awesome!')
            : t('spinResult.tryAgain', 'Try Again Tomorrow')}
        </button>

        {/* Next Spin Info */}
        <p className="mt-4 text-xs text-gray-400">
          {t('spinResult.nextSpin', 'Come back tomorrow for another free spin!')}
        </p>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes confetti {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti 2s ease-out forwards;
        }
        @keyframes bounce-in {
          0% {
            transform: scale(0.3);
            opacity: 0;
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-bounce-in {
          animation: bounce-in 0.5s ease-out;
        }
        @keyframes fade-in {
          0% {
            opacity: 0;
            transform: scale(0.95);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
